import React, { useState } from 'react';

interface PropertyImageCarouselProps {
  images: string[];
  title?: string;
  maxImages?: number;
  height?: string;
  className?: string;
}

export default function PropertyImageCarousel({
  images,
  title = 'Propiedad',
  maxImages = 3,
  height = '200px',
  className = ''
}: PropertyImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState<{ [key: number]: boolean }>({});

  // Limitar las imágenes a mostrar
  const displayImages = images?.slice(0, maxImages) || [];

  // Si no hay imágenes o todas fallaron
  const allImagesFailed = displayImages.every((_, idx) => imageError[idx]);
  if (!displayImages.length || allImagesFailed) {
    return (
      <div
        className={`position-relative bg-light d-flex align-items-center justify-content-center ${className}`}
        style={{ height }}
      >
        <div className="text-center">
          <i className="fa-solid fa-image text-muted fa-2x mb-2"></i>
          <div className="small text-muted">Sin imagen</div>
        </div>
      </div>
    );
  }

  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  const handleImageError = (index: number) => {
    setImageError((prev) => ({ ...prev, [index]: true }));
  };

  return (
    <div className={`position-relative ${className}`} style={{ height }}>
      {/* Imagen actual */}
      {!imageError[currentIndex] && (
        <img
          src={displayImages[currentIndex]}
          alt={`${title} - Imagen ${currentIndex + 1}`}
          className="w-100 h-100 rounded"
          style={{ objectFit: 'cover' }}
          onError={() => handleImageError(currentIndex)}
        />
      )}

      {/* Fallback si imagen falla */}
      {imageError[currentIndex] && (
        <div
          className="w-100 h-100 bg-light d-flex align-items-center justify-content-center rounded"
        >
          <div className="text-center">
            <i className="fa-solid fa-image text-muted fa-2x mb-2"></i>
            <div className="small text-muted">Imagen no disponible</div>
          </div>
        </div>
      )}

      {/* Controles de navegación (solo si hay más de 1 imagen) */}
      {displayImages.length > 1 && (
        <>
          {/* Botón Anterior */}
          <button
            onClick={handlePrev}
            className="btn btn-light btn-sm position-absolute top-50 start-0 translate-middle-y ms-2 opacity-75 hover-opacity-100"
            style={{
              width: '30px',
              height: '30px',
              padding: 0,
              borderRadius: '50%',
              zIndex: 10
            }}
            type="button"
            title="Imagen anterior"
          >
            <i className="fa fa-chevron-left"></i>
          </button>

          {/* Botón Siguiente */}
          <button
            onClick={handleNext}
            className="btn btn-light btn-sm position-absolute top-50 end-0 translate-middle-y me-2 opacity-75 hover-opacity-100"
            style={{
              width: '30px',
              height: '30px',
              padding: 0,
              borderRadius: '50%',
              zIndex: 10
            }}
            type="button"
            title="Siguiente imagen"
          >
            <i className="fa fa-chevron-right"></i>
          </button>

          {/* Indicadores de posición */}
          <div
            className="position-absolute bottom-0 start-50 translate-middle-x mb-2 d-flex gap-1"
            style={{ zIndex: 10 }}
          >
            {displayImages.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentIndex(idx);
                }}
                className={`rounded-circle border-0 ${
                  idx === currentIndex ? 'bg-primary' : 'bg-light opacity-75'
                }`}
                style={{
                  width: '8px',
                  height: '8px',
                  padding: 0,
                  cursor: 'pointer'
                }}
                type="button"
                title={`Ver imagen ${idx + 1}`}
              />
            ))}
          </div>

          {/* Contador de imágenes */}
          <div
            className="position-absolute top-0 end-0 m-2 badge bg-dark bg-opacity-75"
            style={{ fontSize: '0.7rem', zIndex: 10 }}
          >
            <i className="fa fa-images me-1"></i>
            {currentIndex + 1}/{displayImages.length}
          </div>
        </>
      )}
    </div>
  );
}
